import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import namkeLogoHeader from '../assets/namke-logo-header.png';

const Sidebar = ({ isOpen, onClose, onCartClick }) => {
  const { t } = useTranslation();
  const { cartItems } = useCart();
  const cartCount = cartItems.length;
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access_token'));

  // Auth state sync
  useEffect(() => {
    const handleStorage = () => setIsAuthenticated(!!localStorage.getItem('access_token'));
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    onClose();
  };



  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[99] ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-80 bg-namke-white shadow-2xl transform transition-transform duration-300 ease-in-out z-[101] backdrop-blur-none flex flex-col font-sans ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-namke-darkGreen/10 shrink-0">
          <div className="flex items-center gap-3">
            <img src={namkeLogoHeader} alt="namke" className="h-9 w-auto object-contain" />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-namke-olive/15 rounded-full transition-colors"
          >
            <X size={20} className="text-namke-darkGreen" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 min-h-0 bg-namke-white overflow-y-auto">

          {/* Navigation */}
          <div className="flex-1 p-4 space-y-1 bg-namke-white">
            {/* Accueil */}
            <Link
              to="/"
              onClick={onClose}
              className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              {t('nav.home')}
            </Link>

            {/* Restaurants */}
            <Link
              to="/restaurants"
              onClick={onClose}
              className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              {t('nav.restaurants')}
            </Link>

            {/* Services traiteurs */}
            <Link
              to="/traiteur"
              onClick={onClose}
              className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              {t('nav.cateringService')}
            </Link>

            {/* Grocery shop */}
            <Link
              to="/store"
              onClick={onClose}
              className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              {t('nav.groceryShop')}
            </Link>

            {/* À propos */}
            <Link
              to="/about-us"
              onClick={onClose}
              className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              {t('sidebar.aboutUs')}
            </Link>
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-namke-darkGreen/10 space-y-3 bg-namke-white">
            {/* Panier */}
            <button
              type="button"
              onClick={() => { onClose(); onCartClick?.(); }}
              className="w-full flex items-center justify-between px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
            >
              <span>{t('sidebar.cart')}</span>
              {cartCount > 0 && (
                <span className="bg-namke-terracotta text-namke-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Authentification */}
            {isAuthenticated ? (
              <div className="space-y-2">
                <Link
                  to="/profile"
                  onClick={onClose}
                  className="block px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
                >
                  {t('nav.profile')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-namke-black hover:bg-namke-cream rounded-2xl transition-colors text-lg font-medium"
                >
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Link
                  to="/signup"
                  onClick={onClose}
                  className="flex items-center justify-center px-4 py-3 rounded-full bg-namke-darkGreen text-namke-cream text-lg font-semibold hover:bg-namke-black hover:text-namke-mustard transition-colors"
                >
                  {t('nav.signup')}
                </Link>
                <Link
                  to="/login"
                  onClick={onClose}
                  className="flex items-center justify-center px-4 py-3 rounded-full border-2 border-namke-darkGreen/20 text-namke-darkGreen text-lg font-semibold hover:bg-namke-olive/15 hover:border-namke-olive transition-colors"
                >
                  {t('nav.login')}
                </Link>
                <div className="pt-1 space-y-1">
                  <Link
                    to="/business/signup"
                    onClick={onClose}
                    className="block px-1 py-2 text-base text-namke-black/65 hover:text-namke-terracotta transition-colors"
                  >
                    {t('sidebar.addMyRestaurant')}
                  </Link>
                  <Link
                    to="/business/login"
                    onClick={onClose}
                    className="block px-1 py-2 text-base text-namke-black/65 hover:text-namke-terracotta transition-colors"
                  >
                    {t('sidebar.restaurantOwnerLogin')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 